// Self-contained, CSS-only looping product preview: Gantt -> Map -> Health.
const CSS = `
@keyframes pf-scene1 { 0%,28%{opacity:1;transform:translateY(0)} 33%,95%{opacity:0;transform:translateY(8px)} 100%{opacity:1;transform:translateY(0)} }
@keyframes pf-scene2 { 0%,28%{opacity:0} 33%,61%{opacity:1} 66%,100%{opacity:0} }
@keyframes pf-scene3 { 0%,61%{opacity:0} 66%,94%{opacity:1} 100%{opacity:0} }
@keyframes pf-grow { from{transform:scaleX(0)} to{transform:scaleX(1)} }
@keyframes pf-pulse { 0%,100%{transform:scale(1);opacity:.85} 50%{transform:scale(1.35);opacity:1} }
@keyframes pf-ring { from{stroke-dashoffset:283} to{stroke-dashoffset:71} }
@keyframes pf-dash { to{stroke-dashoffset:-12} }
.pf-scene{position:absolute;inset:0;padding:22px}
.pf-bar{height:14px;border-radius:7px;transform-origin:left;animation:pf-grow 1.1s cubic-bezier(.22,1,.36,1) both}
.pf-node{animation:pf-pulse 1.8s ease-in-out infinite}
`;

const BARS = [
  { top: 8, left: 30, w: 42, c: "#0044FF", d: "0s" },
  { top: 40, left: 40, w: 34, c: "#0EA5E9", d: ".18s" },
  { top: 72, left: 55, w: 30, c: "#7C3AED", d: ".36s" },
  { top: 104, left: 62, w: 26, c: "#10B981", d: ".54s" },
];
const NODES = [
  { x: 40, y: 46, c: "#2563EB" }, { x: 130, y: 30, c: "#10B981" },
  { x: 130, y: 92, c: "#F59E0B" }, { x: 225, y: 46, c: "#EF4444" },
  { x: 300, y: 70, c: "#10B981" },
];
const EDGES = [[0, 1], [0, 2], [1, 3], [2, 3], [3, 4]];

export default function ProductPreview() {
  return (
    <div className="relative" data-testid="product-preview">
      <style>{CSS}</style>
      <div className="absolute -inset-8 rounded-[2.5rem] blur-3xl" style={{ background: "radial-gradient(circle, rgba(37,99,235,0.28), transparent 70%)" }} aria-hidden />
      <div className="relative rounded-2xl border border-white/10 shadow-2xl bg-white/[0.04] backdrop-blur-md overflow-hidden">
        {/* window chrome */}
        <div className="flex items-center gap-1.5 px-4 h-9 border-b border-white/10 bg-white/[0.05]">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          <span className="ml-3 text-[11px] font-semibold text-slate-400">Project Management Orchestrator — live view</span>
        </div>

        <div className="relative" style={{ height: 260 }}>
          {/* Scene 1: Gantt */}
          <div className="pf-scene" style={{ animation: "pf-scene1 9s infinite" }}>
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-3">Timeline</p>
            <div className="relative" style={{ height: 130 }}>
              {BARS.map((b, i) => (
                <div key={i} className="absolute flex items-center gap-2" style={{ top: b.top, left: 0, right: 0 }}>
                  <div className="h-2 w-14 rounded bg-white/15" />
                  <div className="relative flex-1 h-3.5">
                    <div className="pf-bar" style={{ position: "absolute", left: `${b.left}%`, width: `${b.w}%`, background: b.c, animationDelay: b.d }} />
                  </div>
                </div>
              ))}
              {/* dependency connectors */}
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: .5 }}>
                <path d="M60 15 V47 H68" fill="none" stroke="#94a3b8" strokeWidth="0.6" strokeDasharray="3 2" vectorEffect="non-scaling-stroke" />
              </svg>
            </div>
          </div>

          {/* Scene 2: Map */}
          <div className="pf-scene" style={{ animation: "pf-scene2 9s infinite" }}>
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">Project map & critical path</p>
            <svg width="100%" height="180" viewBox="0 0 340 130" preserveAspectRatio="xMidYMid meet">
              {EDGES.map(([a, b], i) => {
                const crit = (a === 3 || b === 3 || a === 0);
                return <line key={i} x1={NODES[a].x} y1={NODES[a].y} x2={NODES[b].x} y2={NODES[b].y} stroke={crit ? "#EF4444" : "#94a3b8"} strokeWidth={crit ? 2.2 : 1.3} strokeDasharray={crit ? "0" : "5 3"} style={crit ? {} : { animation: "pf-dash 1s linear infinite" }} />;
              })}
              {NODES.map((n, i) => (
                <g key={i}>
                  <rect x={n.x - 22} y={n.y - 13} width="44" height="26" rx="6" fill="#fff" stroke="#e2e8f0" />
                  <rect x={n.x - 22} y={n.y - 13} width="4" height="26" rx="2" fill={n.c} />
                  <circle className="pf-node" cx={n.x + 14} cy={n.y - 6} r="3" fill={n.c} style={{ transformOrigin: `${n.x + 14}px ${n.y - 6}px`, animationDelay: `${i * 0.2}s` }} />
                </g>
              ))}
            </svg>
          </div>

          {/* Scene 3: Health */}
          <div className="pf-scene flex items-center gap-6" style={{ animation: "pf-scene3 9s infinite" }}>
            <div className="relative" style={{ width: 110, height: 110 }}>
              <svg width="110" height="110" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="9" />
                <circle cx="50" cy="50" r="45" fill="none" stroke="#3b82f6" strokeWidth="9" strokeLinecap="round"
                  strokeDasharray="283" transform="rotate(-90 50 50)" style={{ animation: "pf-ring 1.4s cubic-bezier(.22,1,.36,1) both" }} />
                <text x="50" y="55" textAnchor="middle" fontSize="20" fontWeight="800" fill="#ffffff">75%</text>
              </svg>
            </div>
            <div className="space-y-2">
              <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Health</p>
              {[["On track", "#10B981", "16"], ["At risk", "#F59E0B", "3"], ["Off track", "#EF4444", "1"]].map(([l, c, n]) => (
                <div key={l} className="flex items-center gap-2 text-sm">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} />
                  <span className="font-semibold" style={{ color: c }}>{n}</span>
                  <span className="text-slate-400 text-xs">{l}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
